import { useQuery } from "@tanstack/react-query";
import type { Tables } from "@/types/database";
import { supabase } from "@/lib/supabase";
import { CONSUMER_VISIBLE_VENUES } from "@/lib/consumerVisibleVenues";
import { loadMappedDiscoveryFeed, mapDiscoveryEventTeaser, resolveDiscoveryImage } from "@/lib/discoveryFeedMap";
import {
  rpcGetEventsByIds,
  rpcGetUpcomingEventsForVenue,
  type EventAccessFullRow,
  type EventAccessTeaserRow,
} from "@/lib/eventAccessRpc";
import {
  formatDistanceKm,
  getCityCenter,
  haversineKm,
  mapCoords,
} from "@/lib/geo";
import { isValidCityKey } from "@/lib/cityKey";
import {
  DEFAULT_CITY_KEY,
  fetchActiveTargetCities,
  getCityCenterByKey,
} from "@/lib/targetCities";
import { formatEventEndTime, formatPrice, formatTime } from "@/lib/format";
import { formatEventSchedule } from "@/lib/eventSchedule";
import {
  getTodayOpeningMinutes,
  getVenueOpenStatus,
  isOpenNow,
} from "@/lib/venueHours";
import {
  capacityForCategory,
} from "@/lib/popular";
import {
  computeEntityPopular,
  computeEntityTopPick,
  fetchWindowedBadgeCounts,
} from "@/lib/popularityMetrics";
import { parseEventGenres } from "@/lib/genreMapping";
import type { MusicGenre } from "@/lib/musicGenres";
import { computeEventTagFlags } from "@/lib/tagFilters";
import { resolvePulseEventCategory } from "@/lib/eventCategory";
import {
  fetchBadgeRules,
  getBadgeRule,
  type BadgeRuleRow,
} from "@/lib/badgeRules";
import {
  mockGallery,
  mockRating,
  isOutdoorCategory,
} from "@/mocks/event.mocks"; // TODO: Replace with real data
import {
  stockImageAlternatesForEvent,
  stockImageAlternatesForVenue,
  ensureStockFeedAlternates,
} from "@/lib/stockImageAlternates";
import { ensureStockFeedVibe } from "@/lib/stockFeedVibe";
import type { VenueVibe } from "@/lib/generated/stockFeedVibe";
import { resolveVenueVibe } from "@/lib/venueVibe";

async function ensureStockFeedMaps(): Promise<void> {
  await Promise.all([ensureStockFeedAlternates(), ensureStockFeedVibe()]);
}
import defaultImage from "@/assets/event-club.jpg";
import fallbackClub from "@/assets/fallback-club.jpg";
import fallbackBar from "@/assets/fallback-bar.jpg";
import fallbackRooftop from "@/assets/fallback-rooftop.jpg";

export type EventCategory = "Bars" | "Clubs" | "Concerts" | "Rooftop";
export type { MusicGenre as MusicStyle };

export const categories = ["All", "Bars", "Clubs", "Rooftop", "Concerts"] as const;

export type HomeFilter = (typeof categories)[number] | "Suggested";

export type EventStatus = "draft" | "published" | "cancelled";

export const homeFilterPills: { id: HomeFilter; label: string }[] = [
  { id: "All", label: "All" },
  { id: "Suggested", label: "✨ Suggested" },
  { id: "Bars", label: "Bars" },
  { id: "Clubs", label: "Clubs" },
  { id: "Rooftop", label: "Rooftop" },
  { id: "Concerts", label: "Concerts" },
];

export interface PulseEvent {
  id: string;
  city: string;
  venue: string;
  venueId: string | null;
  venueAddress: string | null;
  name: string;
  time: string;
  price: string;
  distance: string;
  distanceKm: number;
  category: EventCategory;
  image: string;
  /** Display-only stock alternates for feed anti-dupe (not persisted overrides). */
  imageAlternates?: string[];
  live?: boolean;
  savedCount: number;
  ticketsSold: number;
  capacity: number;
  popular: boolean;
  description: string;
  descriptionDe: string | null;
  descriptionEn: string | null;
  descriptionFr: string | null;
  descriptionIt: string | null;
  lineup?: string[];
  dressCode: string;
  opens: string;
  starts: string;
  ends: string;
  schedule: string;
  coords: { x: number; y: number };
  lat: number | null;
  lng: number | null;
  entryPrice: number | null;
  currency: string | null;
  startsAtIso: string | null;
  endsAtIso: string | null;
  createdAt: string | null;
  rating: number;
  pulseRating: number | null;
  pulseRatingCount: number;
  musicStyle: MusicGenre;
  genres: MusicGenre[];
  gallery: string[];
  isOutdoor: boolean;
  openingHours: string[];
  isTopPick: boolean;
  isPopular: boolean;
  isNew: boolean;
  status: EventStatus;
  storedMusicStyle: string | null;
  storedGenres: string[];
  /** Freemium discovery teaser — blurred card, no detail navigation. */
  isTeaser?: boolean;
  /** ISO unlock time when staffelung still pending (share-link / venue teaser UX). */
  unlockAtIso?: string | null;
}

/** Demo sandboxes (vendor portal only) are hidden from consumer-facing surfaces. */
export function isPublicVenueRow(row: { is_demo?: boolean | null } | null | undefined): boolean {
  return row?.is_demo !== true;
}

export type FetchVenueOptions = {
  /** When false (default), demo sandboxes return null — for the public app. */
  allowDemo?: boolean;
};

/** PostgREST filter: is_demo IS NOT TRUE (includes null and false). */
export const PUBLIC_VENUE_IS_DEMO_FILTER = "is_demo.is.null,is_demo.eq.false";

export interface OpenVenue {
  id: string;
  name: string;
  /** Optional curated UI label; fall back to name when null/undefined. */
  display_name?: string | null;
  category: EventCategory;
  address: string | null;
  lat: number | null;
  lng: number | null;
  photo_url: string | null;
  opening_hours: string[];
  website: string | null;
  city: string;
  distance: string;
  distanceKm: number;
  todayHours: string;
  image: string;
  createdAt: string | null;
  isPopular: boolean;
  isTopPick: boolean;
  isNew?: boolean;
  /** Freemium discovery teaser — blurred card, no detail navigation. */
  isTeaser?: boolean;
  /** Display-only stock alternates for feed anti-dupe (not persisted overrides). */
  imageAlternates?: string[];
  /** Resolved vibe badge (confirmed ambiance tags → stock fallback). */
  vibe?: VenueVibe;
  /** Count of users who marked this venue as interested. */
  interestedCount?: number;
  googleRating?: number | null;
  pulseRating?: number | null;
  pulseRatingCount?: number;
  descriptionDe?: string | null;
  descriptionEn?: string | null;
  descriptionFr?: string | null;
  descriptionIt?: string | null;
}

export interface PulseVenue {
  id: string;
  name: string;
  /** Optional curated UI label; fall back to name when null/undefined. */
  display_name?: string | null;
  category: EventCategory;
  address: string | null;
  lat: number | null;
  lng: number | null;
  photo_url: string | null;
  opening_hours: string[];
  website: string | null;
  city: string;
  distance: string;
  distanceKm: number;
  todayHours: string;
  image: string;
  imageAlternates?: string[];
  vibe?: VenueVibe;
  interestedCount?: number;
  googleRating: number | null;
  pulseRating: number | null;
  pulseRatingCount: number;
  descriptionDe: string | null;
  descriptionEn: string | null;
  descriptionFr: string | null;
  descriptionIt: string | null;
  isDemo?: boolean;
}

/** Map open-venue catalog rows to full PulseVenue for night roadmap helpers. */
export function openVenueToPulseVenue(venue: OpenVenue): PulseVenue {
  return {
    id: venue.id,
    name: venue.name,
    display_name: venue.display_name ?? null,
    category: venue.category,
    address: venue.address,
    lat: venue.lat,
    lng: venue.lng,
    photo_url: venue.photo_url,
    opening_hours: venue.opening_hours,
    website: venue.website,
    city: venue.city,
    distance: venue.distance,
    distanceKm: venue.distanceKm,
    todayHours: venue.todayHours,
    image: venue.image,
    imageAlternates: venue.imageAlternates,
    vibe: venue.vibe,
    interestedCount: venue.interestedCount,
    googleRating: venue.googleRating ?? null,
    pulseRating: venue.pulseRating ?? null,
    pulseRatingCount: venue.pulseRatingCount ?? 0,
    descriptionDe: venue.descriptionDe ?? null,
    descriptionEn: venue.descriptionEn ?? null,
    descriptionFr: venue.descriptionFr ?? null,
    descriptionIt: venue.descriptionIt ?? null,
  };
}

export type FeedItem =
  | { kind: "event"; event: PulseEvent; distanceKm: number }
  | { kind: "venue"; venue: OpenVenue; distanceKm: number };

const OPEN_VENUE_FIELDS =
  "id, name, display_name, category, address, lat, lng, photo_url, opening_hours, website, city, google_rating, pulse_rating, pulse_rating_count, description_de, description_en, description_fr, description_it, is_demo" as const;

/** Consumer view includes confirmed ambiance_tags (not on raw `venues`). */
const OPEN_VENUE_FIELDS_WITH_TAGS = `${OPEN_VENUE_FIELDS}, ambiance_tags` as const;

type VenueRow = Tables<"venues">;
type EventRow = Tables<"events">;

type EventVenueJoin = Pick<
  VenueRow,
  | "id"
  | "name"
  | "lat"
  | "lng"
  | "city"
  | "category"
  | "photo_url"
  | "opening_hours"
  | "address"
  | "is_demo"
>;

type EventWithVenue = Pick<
  EventRow,
  | "id"
  | "venue_id"
  | "name"
  | "starts_at"
  | "opens_at"
  | "ends_at"
  | "entry_price"
  | "currency"
  | "description"
  | "description_de"
  | "description_en"
  | "description_fr"
  | "description_it"
  | "lineup"
  | "dress_code"
  | "music_style"
  | "genres"
  | "photo_url"
  | "is_live"
  | "capacity"
  | "created_at"
  | "status"
  | "pulse_rating"
  | "pulse_rating_count"
  | "category"
> & {
  venues: EventVenueJoin | null;
};

export const HOME_FEED_PAGE_SIZE = 40;
export const LIVE_GRACE_MS = 6 * 60 * 60 * 1000;
const HOME_FEED_GRACE_MS = 4 * 60 * 60 * 1000;

/** Live if flagged in DB, or started and not ended (6h grace when ends_at is null). */
export function isEventLive(
  row: Pick<EventRow, "ends_at" | "starts_at" | "is_live">,
  now = new Date(),
): boolean {
  if (row.ends_at && new Date(row.ends_at) < now) return false;
  if (row.is_live) return true;
  if (!row.starts_at) return false;

  const starts = new Date(row.starts_at);
  if (starts > now) return false;

  if (row.ends_at) {
    return new Date(row.ends_at) >= now;
  }

  const graceStart = new Date(now.getTime() - LIVE_GRACE_MS);
  return starts >= graceStart;
}

export function isEventLiveFromPulse(event: PulseEvent, now = new Date()): boolean {
  return isEventLive(
    {
      starts_at: event.startsAtIso,
      ends_at: event.endsAtIso,
      is_live: event.live ? true : null,
    },
    now,
  );
}

/** Visible on home feed unless ended (live events always shown). */
export function isVisibleOnHomeFeed(
  row: Pick<EventRow, "ends_at" | "starts_at" | "is_live">,
  now = new Date(),
): boolean {
  if (row.is_live) return true;

  const cutoff = new Date(now.getTime() - HOME_FEED_GRACE_MS);
  if (row.ends_at) {
    return new Date(row.ends_at) >= now;
  }
  if (row.starts_at) {
    return new Date(row.starts_at) >= cutoff;
  }
  return false;
}

function resolveEventGenres(row: EventWithVenue, category: EventCategory): MusicGenre[] {
  return parseEventGenres(
    row.genres,
    row.name,
    row.description ?? "",
    category,
    row.music_style,
  );
}

const CATEGORY_MAP: Record<string, EventCategory> = {
  bars: "Bars",
  bar: "Bars",
  clubs: "Clubs",
  club: "Clubs",
  concerts: "Concerts",
  concert: "Concerts",
  rooftop: "Rooftop",
  rooftops: "Rooftop",
};

function normalizeCategory(raw: string | null | undefined): EventCategory {
  if (!raw) return "Bars";
  const key = raw.trim().toLowerCase();
  return CATEGORY_MAP[key] ?? (categories.includes(raw as EventCategory) ? (raw as EventCategory) : "Bars");
}

/** Re-export for callers / tests — implementation in eventCategory.ts */
export { resolvePulseEventCategory } from "@/lib/eventCategory";

function eventImage(row: EventWithVenue): string {
  const url = row.photo_url ?? row.venues?.photo_url;
  return url?.trim() || defaultImage;
}

function showSlotKey(event: PulseEvent): string {
  return `${event.venue}\0${event.startsAtIso ?? ""}`;
}

function preferPulseEvent(a: PulseEvent, b: PulseEvent): PulseEvent {
  if (a.live && !b.live) return a;
  if (b.live && !a.live) return b;
  const score = (event: PulseEvent) =>
    (event.name.includes("@") ? 100 : 0) + event.name.length;
  return score(a) >= score(b) ? a : b;
}

/** Collapse pagination/id duplicates and same show under different names (e.g. crawler variants). */
export function dedupePulseEvents(events: PulseEvent[]): PulseEvent[] {
  const seenIds = new Set<string>();
  const byId: PulseEvent[] = [];
  for (const event of events) {
    if (seenIds.has(event.id)) continue;
    seenIds.add(event.id);
    byId.push(event);
  }

  const showSlots = new Map<string, PulseEvent>();
  const result: PulseEvent[] = [];
  for (const event of byId) {
    const key = showSlotKey(event);
    const existing = showSlots.get(key);
    if (!existing) {
      showSlots.set(key, event);
      result.push(event);
      continue;
    }
    const winner = preferPulseEvent(event, existing);
    if (winner !== existing) {
      result[result.indexOf(existing)] = winner;
      showSlots.set(key, winner);
    }
  }
  return result;
}

async function fetchInterestedCounts(eventIds: string[]): Promise<Map<string, number>> {
  const counts = new Map<string, number>();
  if (eventIds.length === 0) return counts;

  const { data, error } = await supabase
    .from("interested")
    .select("event_id")
    .in("event_id", eventIds);

  if (error) throw error;

  for (const row of data ?? []) {
    if (!row.event_id) continue;
    counts.set(row.event_id, (counts.get(row.event_id) ?? 0) + 1);
  }
  return counts;
}

async function fetchVenueInterestedCounts(venueIds: string[]): Promise<Map<string, number>> {
  const counts = new Map<string, number>();
  if (venueIds.length === 0) return counts;

  const { data, error } = await supabase
    .from("interested")
    .select("venue_id")
    .in("venue_id", venueIds);

  if (error) throw error;

  for (const row of data ?? []) {
    if (!row.venue_id) continue;
    counts.set(row.venue_id, (counts.get(row.venue_id) ?? 0) + 1);
  }
  return counts;
}

async function fetchTicketCounts(eventIds: string[]): Promise<Map<string, number>> {
  const counts = new Map<string, number>();
  if (eventIds.length === 0) return counts;

  const { data, error } = await supabase.rpc("get_ticket_counts", {
    p_event_ids: eventIds,
  });

  // Anon/guest has no EXECUTE on this SECURITY DEFINER RPC — empty counts, keep event load.
  if (error) {
    console.warn("[fetchTicketCounts] unavailable, using empty counts", error.message);
    return counts;
  }

  for (const row of data ?? []) {
    if (!row.event_id) continue;
    counts.set(row.event_id, Number(row.ticket_count));
  }
  return counts;
}

type EventBatchCounts = {
  interestedCounts: Map<string, number>;
  ticketCounts: Map<string, number>;
  popularInterestCounts: Map<string, number>;
  popularProposalCounts: Map<string, number>;
  topPickInterestCounts: Map<string, number>;
  topPickProposalCounts: Map<string, number>;
};

async function fetchEventBatchCounts(
  eventIds: string[],
  badgeRules: BadgeRuleRow[],
): Promise<EventBatchCounts> {
  const emptyWindow = {
    interestCounts: new Map<string, number>(),
    proposalCounts: new Map<string, number>(),
  };
  const empty = {
    interestedCounts: new Map<string, number>(),
    ticketCounts: new Map<string, number>(),
    popularInterestCounts: new Map<string, number>(),
    popularProposalCounts: new Map<string, number>(),
    topPickInterestCounts: new Map<string, number>(),
    topPickProposalCounts: new Map<string, number>(),
  };
  if (eventIds.length === 0) return empty;

  const popularRule = getBadgeRule(badgeRules, "popular", "event");
  const topPickRule = getBadgeRule(badgeRules, "top_pick", "event");
  const [interestedCounts, ticketCounts, popularCounts, topPickCounts] = await Promise.all([
    fetchInterestedCounts(eventIds),
    fetchTicketCounts(eventIds),
    popularRule?.enabled
      ? fetchWindowedBadgeCounts("event", eventIds, popularRule.windowHours)
      : Promise.resolve(emptyWindow),
    topPickRule?.enabled
      ? fetchWindowedBadgeCounts("event", eventIds, topPickRule.windowHours)
      : Promise.resolve(emptyWindow),
  ]);

  return {
    interestedCounts,
    ticketCounts,
    popularInterestCounts: popularCounts.interestCounts,
    popularProposalCounts: popularCounts.proposalCounts,
    topPickInterestCounts: topPickCounts.interestCounts,
    topPickProposalCounts: topPickCounts.proposalCounts,
  };
}

async function fetchVenueBadgeWindowCounts(
  venueIds: string[],
  badgeRules: BadgeRuleRow[],
): Promise<{
  popular: { interestCounts: Map<string, number>; proposalCounts: Map<string, number> };
  topPick: { interestCounts: Map<string, number>; proposalCounts: Map<string, number> };
}> {
  const empty = {
    interestCounts: new Map<string, number>(),
    proposalCounts: new Map<string, number>(),
  };
  if (venueIds.length === 0) {
    return { popular: empty, topPick: empty };
  }

  const popularRule = getBadgeRule(badgeRules, "popular", "venue");
  const topPickRule = getBadgeRule(badgeRules, "top_pick", "venue");
  const [popular, topPick] = await Promise.all([
    popularRule?.enabled
      ? fetchWindowedBadgeCounts("venue", venueIds, popularRule.windowHours)
      : Promise.resolve(empty),
    topPickRule?.enabled
      ? fetchWindowedBadgeCounts("venue", venueIds, topPickRule.windowHours)
      : Promise.resolve(empty),
  ]);

  return { popular, topPick };
}

function eventCapacity(row: EventWithVenue, category: EventCategory): number {
  if (row.capacity != null && row.capacity > 0) return row.capacity;
  return capacityForCategory(category);
}

function parseStoredMusicStyle(raw: string | null | undefined): string | null {
  const trimmed = raw?.trim();
  return trimmed || null;
}

function parseStoredGenres(raw: string[] | null | undefined): string[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const tags: string[] = [];
  for (const entry of raw) {
    if (typeof entry !== "string") continue;
    const trimmed = entry.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    tags.push(trimmed);
  }
  return tags;
}

function mapRowToPulseEvent(
  row: EventWithVenue,
  interestedCount: number,
  ticketsSold: number,
  distanceKm: number,
  coords: { x: number; y: number },
  genres: MusicGenre[],
  badgeRules: BadgeRuleRow[],
  popularInterestCounts: Map<string, number>,
  popularProposalCounts: Map<string, number>,
  topPickInterestCounts: Map<string, number>,
  topPickProposalCounts: Map<string, number>,
): PulseEvent {
  const venue = row.venues;
  const category = resolvePulseEventCategory(row.category, venue?.category);
  const capacity = eventCapacity(row, category);
  const savedCount = interestedCount;
  const popularRule = getBadgeRule(badgeRules, "popular", "event");
  const topPickRule = getBadgeRule(badgeRules, "top_pick", "event");
  const popular = computeEntityPopular(
    row.id,
    popularInterestCounts,
    popularProposalCounts,
    popularRule,
  );
  const isTopPick = computeEntityTopPick(
    row.id,
    topPickInterestCounts,
    topPickProposalCounts,
    topPickRule,
    row.pulse_rating != null ? Number(row.pulse_rating) : null,
    row.pulse_rating_count ?? 0,
  );
  const image = eventImage(row);
  const baseEvent = {
    id: row.id,
    city: venue?.city ?? "",
    venue: venue?.name ?? "Venue",
    venueId: venue?.id ?? null,
    venueAddress: venue?.address ?? null,
    name: row.name,
    time: formatTime(row.starts_at),
    starts: formatTime(row.starts_at),
    ends: formatEventEndTime(row.starts_at, row.ends_at),
    schedule: formatEventSchedule(row.starts_at),
    startsAtIso: row.starts_at,
    endsAtIso: row.ends_at,
    price: formatPrice(row.entry_price, row.currency),
    distance: formatDistanceKm(distanceKm),
    distanceKm,
    category,
    image,
    imageAlternates: stockImageAlternatesForEvent(row.id),
    live: isEventLive(row),
    savedCount,
    ticketsSold,
    capacity,
    popular,
    description: row.description ?? "",
    descriptionDe: row.description_de,
    descriptionEn: row.description_en,
    descriptionFr: row.description_fr,
    descriptionIt: row.description_it,
    lineup: row.lineup ?? undefined,
    dressCode: row.dress_code ?? "—",
    opens: formatTime(row.opens_at),
    coords,
    lat: venue?.lat ?? null,
    lng: venue?.lng ?? null,
    entryPrice: row.entry_price,
    currency: row.currency,
    createdAt: row.created_at,
    pulseRating: row.pulse_rating != null ? Number(row.pulse_rating) : null,
    pulseRatingCount: row.pulse_rating_count ?? 0,
    rating:
      row.pulse_rating != null
        ? Number(row.pulse_rating)
        : mockRating(row.id),
    musicStyle: genres[0] ?? "Pop",
    genres,
    gallery: mockGallery(image, row.id),
    isOutdoor: isOutdoorCategory(category),
    openingHours: venue?.opening_hours ?? [],
    status: (row.status as EventStatus) ?? "published",
    storedMusicStyle: parseStoredMusicStyle(row.music_style),
    storedGenres: parseStoredGenres(row.genres),
  } satisfies Omit<PulseEvent, "isTopPick" | "isPopular" | "isNew">;

  return {
    ...baseEvent,
    ...computeEventTagFlags({ ...baseEvent, isTopPick } as PulseEvent, badgeRules),
  };
}

function computeDistanceKm(venue: Pick<VenueRow, "lat" | "lng"> | null, city: string): number {
  const km = computeVenueDistanceKm(venue, city);
  return km ?? 0;
}

export function computeVenueDistanceKm(
  venue: Pick<VenueRow, "lat" | "lng"> | null,
  cityKey: string,
  userLat?: number | null,
  userLng?: number | null,
): number | null {
  if (venue?.lat == null || venue?.lng == null) return null;
  if (userLat != null && userLng != null) {
    return haversineKm(userLat, userLng, venue.lat, venue.lng);
  }
  const center = getCityCenterByKey(cityKey) ?? getCityCenter(cityKey);
  if (!center) return null;
  return haversineKm(center.lat, center.lng, venue.lat, venue.lng);
}

export function enrichEventsWithUserDistance(
  events: PulseEvent[],
  city: string,
  userLat?: number | null,
  userLng?: number | null,
): PulseEvent[] {
  return events.map((event) => {
    if (event.isTeaser) return event;
    const km = computeVenueDistanceKm(event, city, userLat, userLng);
    if (km == null) return event;
    return { ...event, distanceKm: km, distance: formatDistanceKm(km) };
  });
}

/** Enrich OpenVenue rows (e.g. from get_discovery_feed) with distance from user or city center. */
export function enrichOpenVenuesWithDistance(
  venues: OpenVenue[],
  city: string,
  userLat?: number | null,
  userLng?: number | null,
): OpenVenue[] {
  return venues.map((venue) => {
    if (venue.isTeaser) return venue;
    const km = computeVenueDistanceKm(venue, city || venue.city, userLat, userLng);
    if (km == null) return venue;
    return { ...venue, distanceKm: km, distance: formatDistanceKm(km) };
  });
}

export function getEventDistanceLabel(
  event: Pick<PulseEvent, "distance" | "distanceKm" | "lat" | "lng">,
): string | null {
  if (event.lat == null || event.lng == null) return null;
  if (event.distanceKm <= 0 || !event.distance.trim()) return null;
  return event.distance;
}

function computeOpenVenueDistanceKm(
  venue: Pick<VenueRow, "lat" | "lng">,
  city: string,
  userLat?: number | null,
  userLng?: number | null,
): number {
  return computeVenueDistanceKm(venue, city, userLat, userLng) ?? 0;
}

function computeMapBounds(venues: Pick<VenueRow, "lat" | "lng">[]): {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
} {
  const withCoords = venues.filter((v) => v.lat != null && v.lng != null);
  if (withCoords.length === 0) {
    return { minLat: 0, maxLat: 1, minLng: 0, maxLng: 1 };
  }
  return {
    minLat: Math.min(...withCoords.map((v) => v.lat!)),
    maxLat: Math.max(...withCoords.map((v) => v.lat!)),
    minLng: Math.min(...withCoords.map((v) => v.lng!)),
    maxLng: Math.max(...withCoords.map((v) => v.lng!)),
  };
}

/**
 * Normalize a feed city key. Invalid / empty keys used to query `city_key=eq.<junk>`
 * and return `[]`, which the UI treated as a genuine empty market. Default to
 * Zurich (currently the only active market) instead of a silent empty feed.
 */
export function resolveFeedCityKey(raw: string | null | undefined): string {
  const key = raw?.trim().toLowerCase() ?? "";
  if (!key || !isValidCityKey(key)) return DEFAULT_CITY_KEY;
  return key;
}

/** City event catalog via get_discovery_feed (same truth as Home/Map — no parallel table path). */
export async function fetchEventsForCity(city: string): Promise<PulseEvent[]> {
  const mapped = await loadMappedDiscoveryFeed(resolveFeedCityKey(city));
  return mapped.events;
}

export function filterByRadiusKm<T extends { distanceKm: number }>(
  items: T[],
  radiusKm: number,
): T[] {
  return items.filter((item) => item.distanceKm <= radiusKm);
}

export async function fetchEventById(id: string): Promise<PulseEvent | null> {
  const map = await fetchEventsByIds([id]);
  return map.get(id) ?? null;
}

function asOptionalString(value: unknown): string | null {
  if (value == null) return null;
  const s = String(value);
  return s.length > 0 ? s : null;
}

function asOptionalNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function rpcFullToEventWithVenue(row: EventAccessFullRow): EventWithVenue {
  const venueId = asOptionalString(row.venue_id);
  const openingHours = Array.isArray(row.venue_opening_hours)
    ? (row.venue_opening_hours as string[])
    : null;
  return {
    id: String(row.id),
    venue_id: venueId,
    name: String(row.name ?? ""),
    starts_at: asOptionalString(row.starts_at),
    opens_at: asOptionalString(row.opens_at),
    ends_at: asOptionalString(row.ends_at),
    entry_price: asOptionalNumber(row.entry_price),
    currency: asOptionalString(row.currency),
    description: asOptionalString(row.description),
    description_de: asOptionalString(row.description_de),
    description_en: asOptionalString(row.description_en),
    description_fr: asOptionalString(row.description_fr),
    description_it: asOptionalString(row.description_it),
    lineup: Array.isArray(row.lineup) ? (row.lineup as string[]) : null,
    dress_code: asOptionalString(row.dress_code),
    music_style: asOptionalString(row.music_style),
    genres: Array.isArray(row.genres) ? (row.genres as string[]) : null,
    photo_url: asOptionalString(row.photo_url),
    is_live: typeof row.is_live === "boolean" ? row.is_live : null,
    capacity: asOptionalNumber(row.capacity) ?? 0,
    created_at: asOptionalString(row.created_at),
    status: (asOptionalString(row.status) as EventStatus | null) ?? "published",
    pulse_rating: asOptionalNumber(row.pulse_rating),
    pulse_rating_count: asOptionalNumber(row.pulse_rating_count) ?? 0,
    category: asOptionalString(row.category),
    venues: venueId
      ? {
          id: venueId,
          name: String(row.venue_name ?? "Venue"),
          lat: asOptionalNumber(row.venue_lat),
          lng: asOptionalNumber(row.venue_lng),
          city: String(row.venue_city ?? ""),
          category: asOptionalString(row.venue_category),
          photo_url: asOptionalString(row.venue_photo_url),
          opening_hours: openingHours,
          address: asOptionalString(row.venue_address),
          is_demo: false,
        }
      : null,
  };
}

function mapAccessTeaserToPulseEvent(row: EventAccessTeaserRow): PulseEvent {
  const mapped = mapDiscoveryEventTeaser(row);
  return {
    ...mapped,
    unlockAtIso: row.unlock_at,
    image: resolveDiscoveryImage(row.teaser_image_url, row.category),
  };
}

async function mapAccessFullRowsToPulseEvents(
  fullRows: EventAccessFullRow[],
): Promise<PulseEvent[]> {
  if (fullRows.length === 0) return [];
  await ensureStockFeedMaps();
  const rows = fullRows.map(rpcFullToEventWithVenue);
  const badgeRules = await fetchBadgeRules();
  const batchCounts = await fetchEventBatchCounts(
    rows.map((r) => r.id),
    badgeRules,
  );
  const venues = rows.map((r) => r.venues).filter((v): v is EventVenueJoin => v != null);
  const bounds = computeMapBounds(venues);

  return rows.map((row) => {
    const venue = row.venues;
    const city = venue?.city ?? "";
    const km = computeDistanceKm(venue, city);
    const coords =
      venue?.lat != null && venue?.lng != null
        ? mapCoords(venue.lat, venue.lng, bounds)
        : { x: 50, y: 50 };
    const category = resolvePulseEventCategory(row.category, venue?.category);
    const genres = resolveEventGenres(row, category);
    const event = mapRowToPulseEvent(
      row,
      batchCounts.interestedCounts.get(row.id) ?? 0,
      batchCounts.ticketCounts.get(row.id) ?? 0,
      km,
      coords,
      genres,
      badgeRules,
      batchCounts.popularInterestCounts,
      batchCounts.popularProposalCounts,
      batchCounts.topPickInterestCounts,
      batchCounts.topPickProposalCounts,
    );
    const unlockAt = fullRows.find((f) => f.id === row.id)?.unlock_at;
    return {
      ...event,
      isTopPick: Boolean(fullRows.find((f) => f.id === row.id)?.is_top_pick) || event.isTopPick,
      isNew: Boolean(fullRows.find((f) => f.id === row.id)?.is_new) || event.isNew,
      unlockAtIso: unlockAt == null ? null : String(unlockAt),
      isTeaser: false,
    };
  });
}

export async function fetchEventsByIds(ids: string[]): Promise<Map<string, PulseEvent>> {
  const unique = [...new Set(ids.filter(Boolean))];
  const result = new Map<string, PulseEvent>();
  if (unique.length === 0) return result;

  const { events } = await rpcGetEventsByIds(unique);
  const full = events.filter((e): e is EventAccessFullRow => e.payload_kind === "full");
  const teasers = events.filter((e): e is EventAccessTeaserRow => e.payload_kind === "teaser");

  const mappedFull = await mapAccessFullRowsToPulseEvents(full);
  for (const event of mappedFull) {
    result.set(event.id, event);
  }
  for (const teaser of teasers) {
    result.set(teaser.id, mapAccessTeaserToPulseEvent(teaser));
  }
  return result;
}

function venueImage(row: Pick<VenueRow, "photo_url">, category?: string | null): string {
  if (row.photo_url?.trim()) return row.photo_url.trim();
  switch (category) {
    case "Clubs":
      return fallbackClub;
    case "Bars":
      return fallbackBar;
    case "Rooftop":
      return fallbackRooftop;
    default:
      return defaultImage;
  }
}

function normalizeOpeningHours(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.filter((line): line is string => typeof line === "string" && line.trim().length > 0);
  }
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed)) {
        return parsed.filter((line): line is string => typeof line === "string" && line.trim().length > 0);
      }
    } catch {
      return [raw.trim()];
    }
  }
  return [];
}

function hasOpeningHours(openingHours: string[] | null | undefined): boolean {
  return normalizeOpeningHours(openingHours).length > 0;
}

type VenueSelectRow = Pick<
  VenueRow,
  | "id"
  | "name"
  | "display_name"
  | "category"
  | "address"
  | "lat"
  | "lng"
  | "photo_url"
  | "opening_hours"
  | "website"
  | "city"
  | "google_rating"
  | "pulse_rating"
  | "pulse_rating_count"
  | "description_de"
  | "description_en"
  | "description_fr"
  | "description_it"
  | "is_demo"
> & {
  ambiance_tags?: string[] | null;
};

function mapVenueSelectRow(row: VenueSelectRow): PulseVenue {
  const opening_hours = normalizeOpeningHours(row.opening_hours);
  const category = normalizeCategory(row.category);
  const km = computeDistanceKm(row, row.city);
  return {
    id: row.id,
    name: row.name,
    display_name: row.display_name,
    category,
    address: row.address,
    lat: row.lat,
    lng: row.lng,
    photo_url: row.photo_url,
    opening_hours,
    website: row.website,
    city: row.city,
    distance: formatDistanceKm(km),
    distanceKm: km,
    todayHours: "",
    image: venueImage(row, row.category),
    imageAlternates: stockImageAlternatesForVenue(row.id),
    vibe: resolveVenueVibe(row.id, row.ambiance_tags),
    googleRating: row.google_rating,
    pulseRating: row.pulse_rating != null ? Number(row.pulse_rating) : null,
    pulseRatingCount: row.pulse_rating_count ?? 0,
    descriptionDe: row.description_de,
    descriptionEn: row.description_en,
    descriptionFr: row.description_fr,
    descriptionIt: row.description_it,
    isDemo: row.is_demo === true,
  };
}

export async function fetchVenueById(
  id: string,
  options?: FetchVenueOptions,
): Promise<PulseVenue | null> {
  await ensureStockFeedMaps();

  if (options?.allowDemo) {
    const { data, error } = await supabase
      .from("venues")
      .select(OPEN_VENUE_FIELDS)
      .eq("id", id)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;
    return mapVenueSelectRow(data);
  }

  const { data, error } = await supabase
    .from(CONSUMER_VISIBLE_VENUES as "venues")
    .select(OPEN_VENUE_FIELDS_WITH_TAGS)
    .eq("id", id)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  return mapVenueSelectRow(data as unknown as VenueSelectRow);
}

export async function fetchVenueInterestedCount(venueId: string): Promise<number> {
  const counts = await fetchVenueInterestedCounts([venueId]);
  return counts.get(venueId) ?? 0;
}

export function useVenueInterestedCount(venueId: string) {
  return useQuery({
    queryKey: ["venue-interested-count", venueId],
    queryFn: () => fetchVenueInterestedCount(venueId),
    staleTime: 30_000,
    enabled: !!venueId,
  });
}

export async function fetchVenuesByIds(ids: string[]): Promise<Map<string, PulseVenue>> {
  if (ids.length === 0) return new Map();

  const results = await Promise.all(ids.map((id) => fetchVenueById(id)));
  const map = new Map<string, PulseVenue>();
  for (let i = 0; i < ids.length; i++) {
    const venue = results[i];
    const id = ids[i];
    if (venue && id) map.set(id, venue);
  }
  return map;
}

export async function fetchUpcomingEventsForVenue(venueId: string): Promise<PulseEvent[]> {
  const access = await rpcGetUpcomingEventsForVenue(venueId);
  const fullRows = access.events.filter((e): e is EventAccessFullRow => e.payload_kind === "full");
  const mappedFull = await mapAccessFullRowsToPulseEvents(fullRows);
  const fullById = new Map(mappedFull.map((e) => [e.id, e]));

  return access.events.map((item) => {
    if (item.payload_kind === "teaser") return mapAccessTeaserToPulseEvent(item);
    return fullById.get(item.id) ?? mapAccessTeaserToPulseEvent({
      id: item.id,
      category: String(item.category ?? "Events"),
      teaser_image_url: null,
      is_top_pick: false,
      unlock_at: item.unlock_at ?? null,
      visible: false,
      payload_kind: "teaser",
    });
  });
}

export function venueQueryKey(id: string) {
  return ["venue", id] as const;
}

export function venueEventsQueryKey(venueId: string) {
  return ["venueEvents", venueId] as const;
}

export function useVenue(id: string) {
  return useQuery({
    queryKey: venueQueryKey(id),
    queryFn: () => fetchVenueById(id),
    staleTime: 60_000,
  });
}

export function useVenueEvents(venueId: string) {
  return useQuery({
    queryKey: venueEventsQueryKey(venueId),
    queryFn: () => fetchUpcomingEventsForVenue(venueId),
    staleTime: 60_000,
    enabled: !!venueId,
  });
}

export async function fetchOpenVenuesForCity(
  cityKey: string,
  userLat?: number | null,
  userLng?: number | null,
): Promise<OpenVenue[]> {
  await Promise.all([fetchActiveTargetCities(), ensureStockFeedMaps()]);
  const resolvedCityKey = resolveFeedCityKey(cityKey);

  const venueSelect =
    "id, name, display_name, category, address, lat, lng, photo_url, opening_hours, website, city, created_at, google_rating, pulse_rating, pulse_rating_count, description_de, description_en, description_fr, description_it, ambiance_tags" as const;

  const { data: rawData, error } = await supabase
    .from(CONSUMER_VISIBLE_VENUES as "venues")
    .select(venueSelect)
    .eq("city_key", resolvedCityKey)
    .not("opening_hours", "is", null)
    .order("name", { ascending: true })
    .limit(150);

  if (error) throw error;

  type OpenVenueSelectRow = VenueSelectRow & { created_at?: string | null };
  let rows = ((rawData ?? []) as unknown as OpenVenueSelectRow[]).map((row) => ({
    ...row,
    opening_hours: normalizeOpeningHours(row.opening_hours),
  }));
  if (rows.length === 0) {
    const fallback = await supabase
      .from(CONSUMER_VISIBLE_VENUES as "venues")
      .select(venueSelect)
      .eq("city_key", resolvedCityKey)
      .limit(150);
    if (fallback.error) throw fallback.error;
    rows = ((fallback.data ?? []) as unknown as OpenVenueSelectRow[]).map((row) => ({
      ...row,
      opening_hours: normalizeOpeningHours(row.opening_hours),
    }));
  }

  const normalizedRows = rows;

  const venuesWithHours = normalizedRows.filter((v) => hasOpeningHours(v.opening_hours));
  const usedForPipeline =
    venuesWithHours.length > 0 ? venuesWithHours : normalizedRows;

  if (usedForPipeline.length === 0) return [];

  const venueIds = usedForPipeline.map((row) => row.id);
  const badgeRules = await fetchBadgeRules();
  const popularRule = getBadgeRule(badgeRules, "popular", "venue");
  const topPickRule = getBadgeRule(badgeRules, "top_pick", "venue");
  const { popular, topPick } = await fetchVenueBadgeWindowCounts(venueIds, badgeRules);
  const interestedCounts = await fetchVenueInterestedCounts(venueIds);

  return usedForPipeline
    .map((row) => {
      const km = computeOpenVenueDistanceKm(row, resolvedCityKey, userLat, userLng);
      return {
        id: row.id,
        name: row.name,
        display_name: row.display_name,
        category: normalizeCategory(row.category),
        address: row.address,
        lat: row.lat,
        lng: row.lng,
        photo_url: row.photo_url,
        opening_hours: row.opening_hours ?? [],
        website: row.website,
        city: row.city,
        distance: formatDistanceKm(km),
        distanceKm: km,
        todayHours: "",
        image: venueImage(row, row.category),
        imageAlternates: stockImageAlternatesForVenue(row.id),
        vibe: resolveVenueVibe(
          row.id,
          (row as { ambiance_tags?: string[] | null }).ambiance_tags,
        ),
        interestedCount: interestedCounts.get(row.id) ?? 0,
        createdAt: row.created_at ?? null,
        googleRating: row.google_rating,
        pulseRating: row.pulse_rating != null ? Number(row.pulse_rating) : null,
        pulseRatingCount: row.pulse_rating_count ?? 0,
        descriptionDe: row.description_de,
        descriptionEn: row.description_en,
        descriptionFr: row.description_fr,
        descriptionIt: row.description_it,
        isPopular: computeEntityPopular(
          row.id,
          popular.interestCounts,
          popular.proposalCounts,
          popularRule,
        ),
        isTopPick: computeEntityTopPick(
          row.id,
          topPick.interestCounts,
          topPick.proposalCounts,
          topPickRule,
          row.pulse_rating != null ? Number(row.pulse_rating) : null,
          row.pulse_rating_count ?? 0,
        ),
      } satisfies OpenVenue;
    })
    .sort((a, b) => a.distanceKm - b.distanceKm);
}

export async function fetchMapVenues(
  cityKey: string,
  userLat?: number | null,
  userLng?: number | null,
): Promise<OpenVenue[]> {
  await Promise.all([fetchActiveTargetCities(), ensureStockFeedMaps()]);
  const resolvedCityKey = resolveFeedCityKey(cityKey);

  const venueSelect =
    "id, name, display_name, category, address, lat, lng, photo_url, opening_hours, website, city, created_at, pulse_rating, pulse_rating_count, ambiance_tags" as const;

  const { data: rawData, error } = await supabase
    .from(CONSUMER_VISIBLE_VENUES as "venues")
    .select(venueSelect)
    .eq("city_key", resolvedCityKey)
    .not("lat", "is", null)
    .not("lng", "is", null)
    .limit(150);

  if (error) throw error;
  if (!rawData?.length) return [];

  type MapVenueSelectRow = VenueSelectRow & {
    created_at?: string | null;
    pulse_rating?: number | null;
    pulse_rating_count?: number | null;
  };
  const normalizedRows = (rawData as unknown as MapVenueSelectRow[]).map((row) => ({
    ...row,
    opening_hours: normalizeOpeningHours(row.opening_hours),
  }));

  const withDistance = normalizedRows.map((row) => ({
    row,
    km: computeOpenVenueDistanceKm(row, resolvedCityKey, userLat, userLng),
  }));

  const venueIds = withDistance.map(({ row }) => row.id);
  const badgeRules = await fetchBadgeRules();
  const popularRule = getBadgeRule(badgeRules, "popular", "venue");
  const topPickRule = getBadgeRule(badgeRules, "top_pick", "venue");
  const { popular, topPick } = await fetchVenueBadgeWindowCounts(venueIds, badgeRules);
  const interestedCounts = await fetchVenueInterestedCounts(venueIds);

  return withDistance
    .map(({ row, km }) => ({
      id: row.id,
      name: row.name,
      display_name: row.display_name,
      category: normalizeCategory(row.category),
      address: row.address,
      lat: row.lat,
      lng: row.lng,
      photo_url: row.photo_url,
      opening_hours: row.opening_hours ?? [],
      website: row.website,
      city: row.city,
      distance: formatDistanceKm(km),
      distanceKm: km,
      todayHours: "",
      image: venueImage(row, row.category),
      imageAlternates: stockImageAlternatesForVenue(row.id),
      vibe: resolveVenueVibe(
        row.id,
        (row as { ambiance_tags?: string[] | null }).ambiance_tags,
      ),
      interestedCount: interestedCounts.get(row.id) ?? 0,
      createdAt: row.created_at ?? null,
      isPopular: computeEntityPopular(
        row.id,
        popular.interestCounts,
        popular.proposalCounts,
        popularRule,
      ),
      isTopPick: computeEntityTopPick(
        row.id,
        topPick.interestCounts,
        topPick.proposalCounts,
        topPickRule,
        row.pulse_rating != null ? Number(row.pulse_rating) : null,
        row.pulse_rating_count ?? 0,
      ),
    }) satisfies OpenVenue)
    .sort((a, b) => a.distanceKm - b.distanceKm);
}

type TonightFeedTier = 0 | 1 | 2 | 3 | 4;

function tonightFeedTier(item: FeedItem, now: Date): TonightFeedTier {
  if (item.kind === "event") {
    return isEventLiveFromPulse(item.event, now) ? 0 : 1;
  }

  const { opening_hours, category, name } = item.venue;
  if (isOpenNow(opening_hours, category, now, name)) return 2;
  if (getVenueOpenStatus(opening_hours, category, now, name) === "opens-later") return 3;
  return 4;
}

function compareTonightFeedItems(a: FeedItem, b: FeedItem, now: Date): number {
  const tierA = tonightFeedTier(a, now);
  const tierB = tonightFeedTier(b, now);
  if (tierA !== tierB) return tierA - tierB;

  if (tierA === 1 && a.kind === "event" && b.kind === "event") {
    const startCmp = (a.event.startsAtIso ?? "").localeCompare(b.event.startsAtIso ?? "");
    if (startCmp !== 0) return startCmp;
  }

  if (tierA === 3 && a.kind === "venue" && b.kind === "venue") {
    const openA =
      getTodayOpeningMinutes(a.venue.opening_hours, a.venue.category, now, a.venue.name) ?? 24 * 60;
    const openB =
      getTodayOpeningMinutes(b.venue.opening_hours, b.venue.category, now, b.venue.name) ?? 24 * 60;
    if (openA !== openB) return openA - openB;
  }

  return a.distanceKm - b.distanceKm;
}

export function mergeFeedItems(
  events: PulseEvent[],
  venues: OpenVenue[],
  sortForTonight = false,
): FeedItem[] {
  const items: FeedItem[] = [
    ...events.map((event) => ({ kind: "event" as const, event, distanceKm: event.distanceKm })),
    ...venues.map((venue) => ({ kind: "venue" as const, venue, distanceKm: venue.distanceKm })),
  ];

  if (!sortForTonight) {
    return items.sort((a, b) => {
      if (a.distanceKm !== b.distanceKm) return a.distanceKm - b.distanceKm;
      if (a.kind !== b.kind) return a.kind === "event" ? -1 : 1;
      return 0;
    });
  }

  const now = new Date();
  return items.sort((a, b) => compareTonightFeedItems(a, b, now));
}

/**
 * Reorder items so badged entries lead the list: Top Pick first, then Popular,
 * then New, then everything else. Stable — items within the same tier (including
 * the "rest") keep their original relative order and are not re-sorted.
 */
export function sortByBadgePriority<
  T extends { isTopPick?: boolean; isPopular?: boolean; isNew?: boolean },
>(items: T[]): T[] {
  const tier = (item: T): number => {
    if (item.isTopPick) return 0;
    if (item.isPopular) return 1;
    if (item.isNew) return 2;
    return 3;
  };
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const tierDiff = tier(a.item) - tier(b.item);
      return tierDiff !== 0 ? tierDiff : a.index - b.index;
    })
    .map((entry) => entry.item);
}

export function openVenuesQueryKey(city: string) {
  return ["openVenues", city] as const;
}

export function useOpenVenues(
  city: string,
  options?: {
    enabled?: boolean;
    userLat?: number | null;
    userLng?: number | null;
  },
) {
  return useQuery({
    queryKey: [...openVenuesQueryKey(city), options?.userLat ?? "null", options?.userLng ?? "null"],
    queryFn: () =>
      fetchOpenVenuesForCity(city, options?.userLat ?? undefined, options?.userLng ?? undefined),
    staleTime: 0,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    enabled: options?.enabled ?? true,
  });
}

export function mapVenuesQueryKey(city: string) {
  return ["openVenues", city, "map"] as const;
}

const MAP_VENUES_STALE_MS = 1000 * 60 * 30;

export function useMapVenues(city: string) {
  return useQuery({
    queryKey: mapVenuesQueryKey(city),
    queryFn: () => fetchMapVenues(city),
    staleTime: MAP_VENUES_STALE_MS,
    gcTime: MAP_VENUES_STALE_MS,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
}

export function eventsQueryKey(city: string) {
  return ["events", city] as const;
}

export function eventQueryKey(id: string) {
  return ["event", id] as const;
}

export function useEvents(city: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: eventsQueryKey(city),
    queryFn: () => fetchEventsForCity(city),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    enabled: options?.enabled ?? true,
  });
}

export function useEvent(id: string) {
  return useQuery({
    queryKey: eventQueryKey(id),
    queryFn: () => fetchEventById(id),
    staleTime: 60_000,
  });
}
